import { useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';

import { QueryOption } from '../@types';
import { TemplateActionType, TemplateInput, TemplateResponse, TlcTemplatesListResponse, TLCTemplateDetailsDataType, TLCExecuteDataType } from '../@types/tlc.type';
import { createTLCTemplate, getTemplateActionTypes, getTLCTemplateById, updateTLCTemplate, getTlcTemplates, getTlcTemplateDetails, executeTlcTransactions, TlcExecuteResponse } from '../api/tlc';
import { TLCTemplateActionTypeKey, TLCTemplateKey, TlcTemplateDetailsKey } from '../configs/tlc';

export const useGetAllTlcTemplates = (queryOption: QueryOption): UseQueryResult<TlcTemplatesListResponse> => {
  return useQuery({
    queryKey: [TLCTemplateKey, queryOption.filterModel],
    queryFn: () => getTlcTemplates(queryOption)
  });
};

export const useAddTLCTemplate = (): UseMutationResult<TemplateResponse, Error, TemplateInput> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: TemplateInput) => {
      const response = await createTLCTemplate(data as unknown as TemplateResponse);
      return response;
    },

    onSuccess: async (response: TemplateResponse) => {
      await queryClient.invalidateQueries({
        queryKey: [TLCTemplateKey, response.id]
      });
      await queryClient.invalidateQueries({
        queryKey: [TLCTemplateKey]
      });
    }
  });
};

export const useUpdateTLCTemplate = (id?: number): UseMutationResult<TemplateResponse, Error, TemplateInput> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: TemplateInput) => updateTLCTemplate(Number(data.id) || 0, data as unknown as TemplateResponse),

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [TLCTemplateKey]
      });
      await queryClient.invalidateQueries({
        queryKey: [TLCTemplateKey, id]
      });
    }
  });
};

export const useGetTLCTemplateById = (id: number): UseQueryResult<TemplateResponse | undefined> => {
  return useQuery({
    queryKey: [TLCTemplateKey, id],
    queryFn: () => getTLCTemplateById(id),
    enabled: id !== 0,
    gcTime: 0
  });
};

export const useGetActionTemplateTypes = (): UseQueryResult<TemplateActionType[]> => {
  return useQuery({
    queryKey: [TLCTemplateActionTypeKey],
    queryFn: () => getTemplateActionTypes()
  });
};

export const useGetTlcTemplateDetails = (): UseQueryResult<TLCTemplateDetailsDataType[]> => {
  return useQuery({
    queryKey: [TlcTemplateDetailsKey],
    queryFn: () => getTlcTemplateDetails()
  });
};

export const useExecuteTlcTransactions = (): UseMutationResult<TlcExecuteResponse, Error, TLCExecuteDataType> => {
  return useMutation({
    mutationFn: async (data: TLCExecuteDataType) => {
      return await executeTlcTransactions(data);
    }
  });
};
